/* eslint-disable */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Redirect } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {
	Button,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Typography,
	makeStyles,
} from "@material-ui/core";
import { Page, PageContent, PageHeader, PopupSnackbar } from "frame/components";
import ScheduleEntryDialog from "./schedule-entry-dialog";
import { safetyWiseApi } from "utils/safetyWiseApi";
import { onList as onStaffListRequested } from "features/staff/actions";
import { onListRequested as onSitesListRequested } from "features/sites/actions";
import { isStaffUser } from "utils";
import { useRouter } from "utils/useRouter";
import { HOME_ROUTE } from "features/home/home-routes";
import { ADD_TIMESHEET_ROUTE } from "features/timesheet/timesheet-routes";

const localizer = momentLocalizer(moment);

const useStyles = makeStyles((theme) => ({
	calendarWrapper: {
		height: "calc(100vh - 240px)",
		minHeight: 560,
		background: theme.palette.white,
		borderRadius: 6,
		padding: theme.spacing(1),
	},
	toolbar: {
		display: "flex",
		gap: theme.spacing(2),
		alignItems: "center",
		marginBottom: theme.spacing(2),
		flexWrap: "wrap",
	},
	staffFilter: {
		minWidth: 260,
	},
}));

const statusColorMap = {
	ToDo: "#1976d2",
	InProgress: "#2e7d32",
	Done: "#616161",
	Cancelled: "#c62828",
	Planned: "#1976d2",
	Dispatched: "#1976d2",
};

const getCurrentOrganisationId = (currentOrganisation) => {
	if (!currentOrganisation) return "";
	if (typeof currentOrganisation === "string") return currentOrganisation;
	return currentOrganisation.id || "";
};

const mapEntryToEvent = (entry) => ({
	id: entry.id,
	title: `${entry.title} (${entry.staffFullName || "Unassigned"})${entry.siteName ? ` - ${entry.siteName}` : ""}`,
	start: new Date(entry.startsAtUTC),
	end: new Date(entry.endsAtUTC),
	resource: entry,
});

export const SchedulePage = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const { history } = useRouter();

	const currentOrganisation = useSelector(({ organisations }) => organisations.currentOrganisation);
	const role = useSelector(({ profile }) => profile.role);
	const staffId = useSelector(({ profile }) => profile.staffId);
	const staffResults = useSelector(({ staff }) => staff.pagination?.results || []);
	const siteResults = useSelector(({ sites }) => sites.pagination?.results || []);

	const currentOrganisationId = getCurrentOrganisationId(currentOrganisation);
	const tasksEnabled = useSelector(({ featureFlags }) => featureFlags?.tasks === true);
	const isStaff = isStaffUser(role);

	const [events, setEvents] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [selectedStaffId, setSelectedStaffId] = useState("");
	const [openDialog, setOpenDialog] = useState(false);
	const [selectedEntry, setSelectedEntry] = useState(null);
	const [createRange, setCreateRange] = useState({ start: null, end: null });
	const [snack, setSnack] = useState({ message: "", severity: "success" });
	const [range, setRange] = useState({
		from: moment().startOf("week").toDate(),
		to: moment().endOf("week").toDate(),
	});
	const setRangeThisWeek = () =>
		setRange({
			from: moment().startOf("week").toDate(),
			to: moment().endOf("week").toDate(),
		});

	const setRangeNextWeek = () =>
		setRange({
			from: moment().add(1, "week").startOf("week").toDate(),
			to: moment().add(1, "week").endOf("week").toDate(),
		});

	const setRangeToday = () =>
		setRange({
			from: moment().startOf("day").toDate(),
			to: moment().endOf("day").toDate(),
		});


	const loadSchedule = useCallback(async () => {
		if (!currentOrganisationId) return;
		setIsLoading(true);
		try {
			const payload = {
				fromUTC: moment(range.from).utc().toISOString(),
				toUTC: moment(range.to).utc().toISOString(),
				staffId: selectedStaffId || null,
			};
			const response = await safetyWiseApi.post(`${currentOrganisationId}/schedule/range`, payload);
			const items = Array.isArray(response.data) ? response.data : [];
			setEvents(items.map(mapEntryToEvent));
		} catch (error) {
			setSnack({ message: "Failed to load schedule.", severity: "error" });
		} finally {
			setIsLoading(false);
		}
	}, [currentOrganisationId, range.from, range.to, selectedStaffId]);

	useEffect(() => {
		if (currentOrganisationId) {
			dispatch(onStaffListRequested());
			dispatch(onSitesListRequested());
			loadSchedule();
		}
	}, [dispatch, currentOrganisationId, loadSchedule]);

	useEffect(() => {
		if (isStaff && staffId) {
			setSelectedStaffId(staffId);
		}
	}, [isStaff, staffId]);

	const staffOptions = useMemo(() => {
		return staffResults.map((s) => ({
			id: s.id,
			name: `${s.firstName || ""} ${s.lastName || ""}`.trim() || "Unnamed staff",
		}));
	}, [staffResults]);

	const siteOptions = useMemo(() => {
		return siteResults.map((s) => ({
			id: s.id,
			name: s.siteName || "Unnamed site",
		}));
	}, [siteResults]);

	const defaultStaffId = isStaff ? staffId : selectedStaffId || staffOptions[0]?.id || "";

	const handleSelectSlot = ({ start, end }) => {
		setSelectedEntry(null);
		setCreateRange({ start, end });
		setOpenDialog(true);
	};

	const handleSelectEvent = (event) => {
		setSelectedEntry(event.resource);
		setCreateRange({ start: null, end: null });
		setOpenDialog(true);
	};

	const closeDialog = () => {
		setOpenDialog(false);
		setSelectedEntry(null);
		setCreateRange({ start: null, end: null });
	};

	const startTimesheetFromSchedule = () => {
		if (!selectedEntry || !currentOrganisationId) return;
		safetyWiseApi
			.get(`${currentOrganisationId}/schedule/${selectedEntry.id}/timesheet-handoff`)
			.then((response) => {
				const handoff = response?.data || {};
				const timesheetStart = handoff.timesheetStart || {};
				history.push(ADD_TIMESHEET_ROUTE, {
					scheduleStart: {
						...selectedEntry,
						...handoff,
						startsAtUTC: timesheetStart.startDateTimeUTC || selectedEntry.startsAtUTC,
						siteId:
							timesheetStart.siteId && timesheetStart.siteId !== "00000000-0000-0000-0000-000000000000"
								? timesheetStart.siteId
								: selectedEntry.siteId,
					},
				});
				closeDialog();
			})
			.catch(() => {
				// Keep existing behaviour if handoff fetch fails.
				history.push(ADD_TIMESHEET_ROUTE, { scheduleStart: selectedEntry });
				closeDialog();
			});
	};

	const onRangeChange = (nextRange) => {
		if (Array.isArray(nextRange) && nextRange.length > 0) {
			setRange({ from: nextRange[0], to: nextRange[nextRange.length - 1] });
			return;
		}
		if (nextRange?.start && nextRange?.end) {
			setRange({ from: nextRange.start, to: nextRange.end });
		}
	};

	if (!tasksEnabled) {
		return <Redirect to={HOME_ROUTE} />;
	}

	return (
		<Page title="Schedule">
			<PageHeader title="Schedule" />
			<PageContent pad>
				<div className={classes.toolbar}>
					<Typography variant="body2">
						Plan jobs by week and assign staff.
					</Typography>
					<Button size="small" variant="outlined" onClick={setRangeToday}>
						Today
					</Button>
					<Button size="small" variant="outlined" onClick={setRangeThisWeek}>
						This Week
					</Button>
					<Button size="small" variant="outlined" onClick={setRangeNextWeek}>
						Next Week
					</Button>
					{!isStaff && (
						<FormControl variant="outlined" size="small" className={classes.staffFilter}>
							<InputLabel id="schedule-staff-filter">Filter Staff</InputLabel>
							<Select
								labelId="schedule-staff-filter"
								value={selectedStaffId}
								onChange={(e) => setSelectedStaffId(e.target.value)}
								label="Filter Staff"
							>
								<MenuItem value="">All Staff</MenuItem>
								{staffOptions.map((staff) => (
									<MenuItem key={staff.id} value={staff.id}>
										{staff.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					)}
				</div>

				<div className={classes.calendarWrapper}>
					<Calendar
						localizer={localizer}
						events={events}
						startAccessor="start"
						endAccessor="end"
						view="week"
						views={["week", "day", "agenda"]}
						selectable
						onSelectSlot={handleSelectSlot}
						onSelectEvent={handleSelectEvent}
						onRangeChange={onRangeChange}
						eventPropGetter={(event) => {
							const status = event?.resource?.status || "ToDo";
							const bg = statusColorMap[status] || statusColorMap.ToDo;
							return {
								style: {
									backgroundColor: bg,
									borderColor: bg,
								},
							};
						}}
						components={{
							toolbar: (props) => (
								<div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
									<Button size="small" onClick={() => props.onNavigate("PREV")}>Prev</Button>
									<Button size="small" onClick={() => props.onNavigate("TODAY")}>Today</Button>
									<Button size="small" onClick={() => props.onNavigate("NEXT")}>Next</Button>
									<Typography style={{ marginLeft: 16, fontWeight: 600 }}>{props.label}</Typography>
								</div>
							),
						}}
					/>
				</div>
				{isLoading && <Typography variant="body2" style={{ marginTop: 10 }}>Loading schedule...</Typography>}
			</PageContent>

			<ScheduleEntryDialog
				open={openDialog}
				onClose={closeDialog}
				organisationId={currentOrganisationId}
				entry={selectedEntry}
				initialStart={createRange.start}
				initialEnd={createRange.end}
				defaultStaffId={defaultStaffId}
				staffLocked={isStaff}
				staffOptions={staffOptions}
				siteOptions={siteOptions}
				onSaved={loadSchedule}
				onNotify={setSnack}
				showStartTimesheet={!!selectedEntry}
				onStartTimesheet={startTimesheetFromSchedule}
			/>

			<PopupSnackbar
				message={snack.message}
				error={snack.severity === "error"}
				coloured={snack.severity === "error"}
				onExited={() => setSnack({ message: "", severity: "success" })}
			/>
		</Page>
	);
};

export default SchedulePage;
